// src/components/SyncDashboard.jsx 
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { ProgressBar } from 'primereact/progressbar';
import { Chip } from 'primereact/chip';
import { Divider } from 'primereact/divider';
import { useNavigate, useParams } from 'react-router-dom';
import SyncService from '../service/SyncService';
import Utils from "../utilities/Utils";

const syncService = new SyncService();

const SyncDashboard = () => {
    const iss = localStorage.getItem('iss');

    const [syncData, setSyncData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    // Helper para formatar duração de ms para "Xm Ys"
    const formatDuration = (ms) => {
        if (ms === null || ms === undefined) return 'N/A';
        const totalSeconds = Math.floor(ms / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}m ${seconds}s`;
    };

    // Helper para obter a cor do badge de status
    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'success';
            case 'failed': return 'danger';
            case 'running': return 'info';
            case 'pending': return 'warning';
            default: return 'secondary';
        }
    };

    // Helper para formatar a data ISO para pt-BR
    const formatDateTime = (isoString) => {
        if (!isoString) return 'N/A';
        const date = new Date(isoString);
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Helper para obter label amigável para triggered_by
    const getTriggeredByLabel = (triggeredBy) => {
        switch (triggeredBy) {
            case 'manual': return 'Manual';
            case 'keep-alive-service': return 'Automático';
            default: return triggeredBy;
        }
    };

    // Função para buscar os dados de sincronização
    const fetchSyncStatus = useCallback(async () => {
        if (!iss) {
            setError("ID do perfil não encontrado na URL.");
            setLoading(false);
            setRefreshing(false);
            return;
        }
        try {
            const response = await syncService.getAllSyncStatus(iss);
            setSyncData(response.data);
            setError(null);
        } catch (err) {
            console.error("Erro ao buscar status de sincronização:", err);
            setError("Não foi possível carregar o status da sincronização. Tente novamente.");
            setSyncData(null);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [iss]);

    // Efeito para carregar dados iniciais e configurar polling
    useEffect(() => {
        setLoading(true);
        fetchSyncStatus();

        const interval = setInterval(() => {
            setRefreshing(true);
            fetchSyncStatus();
        }, 30000); // Polling a cada 30 segundos

        return () => clearInterval(interval); // Limpa o intervalo no unmount
    }, [fetchSyncStatus]);

    // Handlers de ações
    const handleSyncNow = useCallback(async (type) => {
        setRefreshing(true);
        try {
            if (type === 'all') {
                await syncService.syncAll(iss, {});
            } else if (type === 'produtos') {
                await syncService.syncProdutos(iss, {});
            } else if (type === 'categorias') {
                await syncService.syncCategorias(iss, {});
            }
            await fetchSyncStatus();
        } catch (error) {
            console.error('Erro ao sincronizar:', error);
            setError('Erro ao sincronizar. Tente novamente.');
        } finally {
            setRefreshing(false);
        }
    }, [iss, setRefreshing, setError, fetchSyncStatus]);

    const handleCancelSync = async (type) => {
        setRefreshing(true);
        try {
            if (type === 'produtos') {
                await syncService.cancelProdutosSync(iss);
            } else if (type === 'categorias') {
                await syncService.cancelCategoriasSync(iss);
            }
            fetchSyncStatus(); // Atualiza o status após cancelar
        } catch (err) {
            setError(`Erro ao cancelar sincronização de ${type}.`);
        } finally {
            setRefreshing(false);
        }
    };

    const handleViewHistory = (type) => {
        // navigate(`/sync/${iss}/${type}/historico`);
    };

    // Renderiza um card de sincronização específico (Categorias ou Produtos)
    const renderSyncCard = (type, data) => {
        if (!data) return null;

        const { isRunning, lastSync } = data;
        const title = type === 'produtos' ? 'Sincronização de Produtos' : 'Sincronização de Categorias';
        const lastSyncStatus = lastSync?.status || 'N/A';
        const statusColor = getStatusColor(lastSyncStatus);
        const totalItems = lastSync?.stats?.total || 0;

        return (
            <div className="col-12 md:col-6">
                <div className="card">
                    <div className="flex justify-content-between align-items-center mb-3">
                        <span className="overview-title">{title}</span>
                        <div className="flex gap-2">
                            {isRunning && (
                                <Badge value="Em Andamento" severity="info" className="mr-2" />
                            )}
                            {lastSyncStatus !== 'N/A' && (
                                <Badge value={lastSyncStatus.charAt(0).toUpperCase() + lastSyncStatus.slice(1)} severity={statusColor} />
                            )}
                        </div>
                    </div>

                    {lastSync ? (
                        <>
                            <div className="overview-detail mb-3">
                                <div className="flex flex-column">
                                    <span className="text-500">Última Sincronização:</span>
                                    <span className="text-900 font-medium text-xl">{formatDateTime(lastSync.completed_at)}</span>
                                </div>
                                <div className="flex flex-column mt-2">
                                    <span className="text-500">Duração:</span>
                                    <span className="text-900 font-medium text-lg">{Utils.formatDuration(lastSync.duration_ms)}</span>
                                </div>
                                <div className="flex flex-column mt-2">
                                    <span className="text-500">Modo:</span>
                                    <Chip label={getTriggeredByLabel(lastSync.triggered_by)} className="mt-1" />
                                </div>
                                {lastSync.error_message && (
                                    <div className="flex flex-column mt-2">
                                        <span className="text-500 text-red-500">Erro:</span>
                                        <span className="text-red-700 font-medium">{lastSync.error_message}</span>
                                    </div>
                                )}
                            </div>

                            <Divider />

                            {isRunning && (
                                <div className="mb-3 p-3 border-round-md text-center">
                                    <i className="pi pi-spin pi-spinner mr-2"></i>
                                    Sincronização em andamento...
                                </div>
                            )}

                            <h4 className="mb-3">Estatísticas Detalhadas:</h4>
                            <div className="grid">
                                {['total', 'inserted', 'updated', 'deleted', 'errors'].map(statKey => {
                                    const value = lastSync.stats?.[statKey] || 0;
                                    const percentage = totalItems > 0 ? ((value / totalItems) * 100).toFixed(1) : 0;
                                    const statLabel = statKey.charAt(0).toUpperCase() + statKey.slice(1);
                                    const statColor = statKey === 'errors' ? 'danger' : 'primary';

                                    return (
                                        <div className="col-12" key={statKey}>
                                            <div className="flex justify-content-between align-items-center">
                                                <span className="text-600">{statLabel}:</span>
                                                <span className="font-medium">{value}</span>
                                            </div>
                                            <ProgressBar value={percentage} showValue={true} unit="%" className={`p-progressbar-${statColor}`} style={{ height: '16px' }} />
                                        </div>
                                    );
                                })}

                                {renderSyncCard === 'produtos' && lastSync.stats && (
                                    <>
                                        <Divider className="col-12" />
                                        <h4 className="col-12 mb-2">Detalhes de Produtos:</h4>
                                        <div className="col-12 md:col-6">
                                            <div className="flex justify-content-between align-items-center">
                                                <span className="text-600">Com Imagem:</span>
                                                <span className="font-medium">{lastSync.stats.products_with_image || 0}</span>
                                            </div>
                                        </div>
                                        <div className="col-12 md:col-6">
                                            <div className="flex justify-content-between align-items-center">
                                                <span className="text-600">Com Estoque:</span>
                                                <span className="font-medium">{lastSync.stats.products_with_stock || 0}</span>
                                            </div>
                                        </div>
                                        <div className="col-12 md:col-6">
                                            <div className="flex justify-content-between align-items-center">
                                                <span className="text-600">Sem Imagem:</span>
                                                <span className="font-medium">{lastSync.stats.products_without_image || 0}</span>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </>
                    ) : (
                        <p className="text-500">Nenhuma sincronização recente encontrada.</p>
                    )}

                    <Divider />

                    <div className="flex justify-content-end gap-2">
                        <Button
                            label={`Sincronizar ${type === 'produtos' ? 'Produtos' : 'Categorias'}`}
                            icon="pi pi-sync"
                            className="p-button-primary p-button-sm mr-2"
                            onClick={() => handleSyncNow(type)}
                            disabled={refreshing}
                        />
                        {/* {isRunning && (
                            <Button
                                label="Cancelar"
                                icon="pi pi-times"
                                className="p-button-danger p-button-sm"
                                onClick={() => handleCancelSync(type)}
                                disabled={refreshing}
                            />
                        )} */}
                        {/* <Button
                            label="Ver Histórico"
                            icon="pi pi-history"
                            className="p-button-secondary p-button-sm"
                            onClick={() => handleViewHistory(type)}
                            disabled={refreshing}
                        /> */}
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="layout-dashboard flex justify-content-center align-items-center" style={{ height: 'calc(100vh - 100px)' }}>
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                <span className="ml-3">Carregando status de sincronização...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="layout-dashboard flex justify-content-center align-items-center" style={{ height: 'calc(100vh - 100px)' }}>
                <div className="card p-4 text-center">
                    <i className="pi pi-exclamation-triangle text-red-500" style={{ fontSize: '2rem' }}></i>
                    <p className="text-red-500 mt-3">{error}</p>
                    <Button label="Tentar Novamente" icon="pi pi-refresh" onClick={() => { setLoading(true); fetchSyncStatus(); }} className="p-button-sm mt-3" />
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="mb-3">
                <Button
                    label="Sincronizar Tudo"
                    icon="pi pi-sync"
                    className="p-button-primary p-button-sm mr-2"
                    onClick={() => handleSyncNow('all')}
                    disabled={refreshing}
                    loading={refreshing}
                />
                {/* {(syncData?.categorias?.isRunning || syncData?.produtos?.isRunning) && (
                    <Button
                        label="Cancelar"
                        icon="pi pi-times"
                        className="p-button-danger p-button-sm"
                        onClick={() => handleCancelSync(syncData?.categorias?.isRunning ? 'categorias' : 'produtos')}
                        disabled={refreshing}
                    />
                )} */}
            </div>
            <div className="grid">
                {syncData?.categorias && renderSyncCard('categorias', syncData.categorias)}
                {syncData?.produtos && renderSyncCard('produtos', syncData.produtos)}
            </div>
            {refreshing && (
                <div className="fixed bottom-0 right-0 p-3 m-3 bg-blue-500 text-white border-round-md shadow-2">
                    <i className="pi pi-spin pi-refresh mr-2"></i>
                    Atualizando dados...
                </div>
            )}
        </div>
    );


};

export default SyncDashboard;